import { ArrowDownTrayIcon, EyeIcon } from "@vericus/cadmus-icons";
import { Button, Pill, Text, vars } from "@vericus/cadmus-ui";

import clsx from "clsx";
import { format } from "date-fns";

import squigglyNoteIcon from "@/assets/illustrations/squiggly-note.svg";
import {
  FeedbackVisibilityFragment,
  SubmissionType,
  useSimilarityQuery,
} from "@/generated/graphql";
import { useDownloadFeedbackPdf } from "@/hooks/use-download-feedback-pdf";

import {
  FeedbackItemContent,
  getVisibilityConstraints,
} from "../utils/feedback-visibility";
import * as styles from "./feedback-item.css";

interface FeedbackItemProps {
  id: FeedbackItemContent;
  title: string;
  /* When feedbackVisibility is null, the WorkOutcomeRelease
  does not exist for the component. */
  feedbackVisibility: FeedbackVisibilityFragment | null;
  weight: number | null;
  maxGrade?: number | null;
  finalScore?: number | null;
  feedbackReleased: boolean;
  onNavigateToFeedback: () => void;
  workId?: string;
  tenant: string;
}

const FALLBACK_VISIBILITY_CONSTRAINTS = {
  showMarks: false,
  showFeedbackCta: false,
  showGrades: false,
  disableAccess: false,
  canDownLoadPdf: false,
} as const;

export function FeedbackItem({
  id,
  title,
  weight,
  feedbackVisibility,
  onNavigateToFeedback,
  feedbackReleased,
  finalScore,
  maxGrade,
  workId,
  tenant,
}: FeedbackItemProps) {
  const {
    showMarks,
    showFeedbackCta,
    showGrades,
    disableAccess,
    canDownLoadPdf,
  } = !feedbackVisibility
    ? FALLBACK_VISIBILITY_CONSTRAINTS
    : getVisibilityConstraints(id, feedbackVisibility);

  const downloadPdf = useDownloadFeedbackPdf(workId ?? "", tenant);
  const isDownloadable = !!workId && canDownLoadPdf;

  const { data } = useSimilarityQuery({
    variables: {
      workId: workId ?? "",
      submissionType: SubmissionType.Final,
    },
    skip: !workId,
  });
  const similarity = data?.latestResult.similarity;

  const status = feedbackReleased
    ? {
        content: "Feedback released",
        bg: vars.colors.successBg,
        color: "#145252",
      }
    : {
        content: "Pending release",
        bg: vars.colors.yellow200,
        color: vars.colors.yellow800,
      };

  const getScore = (score?: number | null) => {
    if (score === null || score === undefined) return "-";

    return Math.trunc(score);
  };

  return (
    <div className={styles.feedbackItem}>
      <div className={styles.feedbackTitle}>
        <Text kind="headingFour">{title}</Text>
        {!!similarity && (
          <Text kind="label">{similarity.display}% similarity score</Text>
        )}
        <div className={styles.feedbackStatus}>
          <Pill color={status.bg} textColor={status.color}>
            {status.content}
          </Pill>
          {data?.latestResult.returnedAt && (
            <Text kind="bodySm">
              {format(new Date(data?.latestResult.returnedAt), "dd MMM yyyy")}
            </Text>
          )}
        </div>
      </div>
      <div className={styles.feedbackItemOptions}>
        {showMarks && (
          <div className={clsx(styles.gradeContent, styles.feedbackGrade)}>
            <img src={squigglyNoteIcon} width={39} height={32} alt="" />
            <div>
              <Text kind="headingFour">
                {showGrades
                  ? `${getScore(finalScore)}/${getScore(maxGrade)}`
                  : `xx/${getScore(maxGrade)}`}
              </Text>
              <Text kind="label">{weight ?? 0}% weighting</Text>
            </div>
          </div>
        )}

        <div className={styles.feedbackActions}>
          {showFeedbackCta && (
            <Button
              disabled={!feedbackReleased || disableAccess}
              kind="secondary"
              onClick={onNavigateToFeedback}
              icon={<EyeIcon size={24} label="" />}
            >
              View feedback
            </Button>
          )}
          {isDownloadable && (
            <Button
              style={{ width: "fit-content" }}
              icon={<ArrowDownTrayIcon size={24} label="" />}
              kind="outline"
              onClick={downloadPdf}
              title="Download Feedback PDF"
              aria-label="Download Feedback PDF"
              disabled={!feedbackReleased || disableAccess}
            />
          )}
        </div>
      </div>
    </div>
  );
}
